import { ApolloClient } from "@apollo/client";
import {
  RemoveCmpNewsletterCustomAudienceRecipientDocument,
  RemoveCmpNewsletterCustomAudienceRecipientMutation,
  RemoveCmpNewsletterCustomAudienceRecipientMutationVariables,
} from "../../../graphql/types";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../utils/apolloCallerContext";

export const CUSTOM_AUDIENCE_RECIPIENT_REMOVAL_UNAVAILABLE_MESSAGE =
  "Removing a recipient from a saved custom audience is not yet available.";

export const CUSTOM_AUDIENCE_RECIPIENT_REMOVAL_FAILED_MESSAGE =
  "The recipient could not be removed from this list. Try again.";

export type RemoveRecipientFromCustomAudienceInput = {
  chamberId: string;
  /** The saved custom audience (recipient group) ID. */
  criterionId: string;
  /** The recipient's lower-case email address, which keys the exclusion. */
  recipientKey: string;
  /** The Apollo client from `useApolloClient()` in the calling component. */
  client?: Pick<ApolloClient<object>, "mutate">;
};

/**
 * Stores a per-list exclusion (mutation removeCmpNewsletterCustomAudienceRecipient).
 * The backend appends the lower-case email idempotently, drops it from the
 * list's additional recipients, and applies the exclusion to the preview, the
 * estimate and the send. Refetch the lists (or the list preview) afterwards.
 */
export const removeRecipientFromCustomAudience = async ({
  chamberId,
  client,
  criterionId,
  recipientKey,
}: RemoveRecipientFromCustomAudienceInput): Promise<void> => {
  if (!client) {
    throw new Error(CUSTOM_AUDIENCE_RECIPIENT_REMOVAL_UNAVAILABLE_MESSAGE);
  }
  const result = await client.mutate<
    RemoveCmpNewsletterCustomAudienceRecipientMutation,
    RemoveCmpNewsletterCustomAudienceRecipientMutationVariables
  >({
    mutation: RemoveCmpNewsletterCustomAudienceRecipientDocument,
    variables: {
      input: {
        chamberId,
        criterionId,
        recipientKey: recipientKey.trim().toLowerCase(),
      },
    },
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  const saved = result.data?.removeCmpNewsletterCustomAudienceRecipient;
  if (!saved?.groupSavedSuccessfully || !saved.group) {
    throw new Error(CUSTOM_AUDIENCE_RECIPIENT_REMOVAL_FAILED_MESSAGE);
  }
};
